import { Check } from 'lucide-react';
import type { FlowRowState } from '../types';

/**
 * The mark on a flow rail: a ring, a tick, a live dot, or a slash.
 *
 * ── Why it exists ──
 *
 * The spine and the sample sub-stepper drew this twice, and both got the
 * geometry wrong the same way: an ODD container (15px and 13px) around an EVEN
 * 4px dot, which centres at 5.5px and 4.5px. Flex centring then lands the dot on
 * a half pixel and the browser rounds it one way or the other, so the dot sits
 * visibly off-centre inside its own ring.
 *
 * `OrderOverview`'s progress matrix had it right and said why:
 *
 *   "Every inner size divides evenly into its container, so the dot and the tick
 *    land on whole pixels. Halve one of these and the dot sits visibly
 *    off-centre."
 *
 * So the rule is a constraint of this component rather than a comment somebody
 * has to remember: `size` must be EVEN, and every inner size is derived from it
 * — dot 4, tick and slash `size - 4` — which keeps both margins whole.
 *
 * ── Why the matrix still has its own ──
 *
 * It looks different on purpose, not accidentally: it sits on `surface-sunken`
 * rather than a card, so an upcoming node needs an opaque `bg-3` ground and its
 * skip mark an opaque one, and a current node carries an `accent-soft` halo that
 * would be noise on a vertical rail. Folding three genuinely different marks
 * into one component would mean three flags, which is how the last shared thing
 * grew a rule nobody could follow. Two identical ones is the merge worth making.
 */
export function StepDot({
	size,
	state,
	/** Dimmed as a whole — the matrix's sub-rows read quieter than their parent. */
	muted,
}: {
	/** Must be even. 14 on the spine, 12 in a sample round. */
	size: number;
	state: FlowRowState;
	muted?: boolean;
}) {
	const done = state === 'done';
	const active = state === 'active';
	const skipped = state === 'skipped';
	const outstanding = state === 'outstanding';
	// Both derived, so both margins stay whole: (size - 4) / 2 and (size - inner) / 2.
	const dot = 4;
	const inner = size - 4;

	return (
		<span
			className="flex shrink-0 items-center justify-center rounded-full"
			style={{
				height: size,
				width: size,
				background: done ? 'var(--accent)' : 'transparent',
				boxShadow: done
					? undefined
					: `inset 0 0 0 1.5px ${
							active
								? 'var(--accent)'
								: outstanding
									? 'var(--status-warn)'
									: 'var(--line-3)'
						}`,
				opacity: skipped ? 0.5 : muted ? 0.85 : 1,
			}}
		>
			{done && <Check size={inner} strokeWidth={3} style={{ color: 'var(--accent-fg)' }} />}
			{/* A ring in the warn tone, and nothing inside it.
			    An overtaken step is not in progress, so it must not wear the live
			    dot, and nothing happened there, so it must not wear a tick. What is
			    left is the empty ring an upcoming step already has — the colour is
			    what says this one was reached and left open. The reason beside the
			    label does the rest; the dot's job is only to stop it reading as
			    done at a glance. */}
			{active && (
				<span
					className="block rounded-full"
					style={{ height: dot, width: dot, background: 'var(--accent)' }}
				/>
			)}
			{/* A slash rather than a fainter ring, so "never applied" cannot be read
			    as "not yet" — the same mark the progress matrix uses, at rail size.
			    Drawn on a 12-unit grid so the stroke stays crisp at any size. */}
			{skipped && (
				<svg width={inner} height={inner} viewBox="0 0 12 12" fill="none" aria-hidden>
					<line
						x1="3"
						y1="9"
						x2="9"
						y2="3"
						stroke="var(--text-4)"
						strokeWidth="1.8"
						strokeLinecap="round"
					/>
				</svg>
			)}
		</span>
	);
}
